import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { Box, HStack, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, rem, Stack, Text, TextProps } from '@bonusly/design-system';

import { useHomepageRedesignEnabled } from '../../../modules/bonus-feed/features/recognition/components/BonusFeed/hooks/useHomepageRedesignEnabled';
import { UserAnnouncements } from '../types/announcements';
import { EditAnnouncementButton } from './edit-announcement-button';

export interface ManageAnnouncementsModalProps {
	isOpen: boolean;
	onClose: VoidFunction;
	userAnnouncements?: UserAnnouncements;
}

export const ManageAnnouncementsModal: FC<ManageAnnouncementsModalProps> = ({ isOpen, onClose, userAnnouncements }) => {
	const { t } = useTranslation('announcements');
	const { track } = useAnalytics();
	const isHomepageRedesignEnabled = useHomepageRedesignEnabled();
	const noUserAnnouncements = !userAnnouncements || (userAnnouncements.scheduled.length === 0 && userAnnouncements.posted.length === 0);

	useEffect(() => {
		// track only once when the modal is opened
		if (noUserAnnouncements || !isOpen) return;
		track(AnalyticsEvent.Announcements.ManageAnnouncementsModalViewed);
	}, [isOpen, noUserAnnouncements, track]);

	if (noUserAnnouncements) return null;

	return (
		<Modal
			autoFocus={false}
			isOpen={isOpen}
			onClose={() => {
				track(AnalyticsEvent.Announcements.ManageAnnouncementsModalClosed);
				onClose();
			}}
			scrollBehavior="inside"
			size={{ base: 'full', md: 'xl' }}
		>
			<ModalOverlay />
			<ModalContent borderRadius={{ base: 0, md: 'xl' }}>
				<ModalHeader
					{...(!isHomepageRedesignEnabled && {
						borderBottomWidth: rem(1),
						borderColor: 'border.soft',
						borderStyle: 'solid',
					})}
					{...(isHomepageRedesignEnabled && {
						px: rem(24),
						pt: rem(16),
						pb: rem(12),
						fontSize: 'xl',
						fontWeight: 'semibold',
						borderBottomWidth: 0,
					})}
					color="text.default"
				>
					<HStack justify="space-between" w="100%">
						<Text as="h2" fontSize="xl" fontWeight="semibold" lineHeight={8}>
							{t('modal.manage_header')}
						</Text>
						<ModalCloseButton data-testid="close-button" h={rem(32)} w={rem(32)} pos="static" borderRadius="full" />
					</HStack>
				</ModalHeader>

				<ModalBody px={5} pt={isHomepageRedesignEnabled ? 0 : 5} pb={5}>
					<Stack spacing={5}>
						{userAnnouncements.scheduled.length > 0 && (
							<Box>
								<HeaderText>{t('modal.scheduled')}</HeaderText>
								{userAnnouncements.scheduled.map((announcement) => (
									<EditAnnouncementButton key={announcement.id} announcement={announcement} type="scheduled" />
								))}
							</Box>
						)}
						{userAnnouncements.posted.length > 0 && (
							<Box>
								<HeaderText>{t('modal.posted')}</HeaderText>
								{userAnnouncements.posted.map((announcement) => (
									<EditAnnouncementButton key={announcement.id} announcement={announcement} type="posted" />
								))}
							</Box>
						)}
					</Stack>
				</ModalBody>
			</ModalContent>
		</Modal>
	);
};

const HeaderText = (props: TextProps) => {
	return <Text color="text.softest" fontSize={100} fontWeight="semibold" lineHeight={5} {...props} />;
};
